import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Users, AlertTriangle } from 'lucide-react';
import { getDrivers, getVehicles, createDriver, updateDriver, deleteDriver } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const SIM_TYPES = ['B1', 'B2', 'C'];
const DRIVER_STATUSES = [
    { value: 'aktif', label: 'Aktif' },
    { value: 'cuti', label: 'Cuti' },
    { value: 'resign', label: 'Resign' }
];

export default function DriversPage() {
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDriver, setEditingDriver] = useState(null);
    const [formData, setFormData] = useState({
        nama: '',
        nik: '',
        nomor_sim: '',
        jenis_sim: 'B2',
        tanggal_expired_sim: '',
        nomor_hp: '',
        alamat: '',
        tanggal_bergabung: '',
        status: 'aktif',
        vehicle_id: ''
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            
            const [driversRes, vehiclesRes] = await Promise.all([
                getDrivers(params),
                getVehicles()
            ]);
            setDrivers(driversRes.data);
            setVehicles(vehiclesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (driver = null) => {
        if (driver) {
            setEditingDriver(driver);
            setFormData({
                nama: driver.nama || '',
                nik: driver.nik || '',
                nomor_sim: driver.nomor_sim || '',
                jenis_sim: driver.jenis_sim || 'B2',
                tanggal_expired_sim: driver.tanggal_expired_sim ? driver.tanggal_expired_sim.split('T')[0] : '',
                nomor_hp: driver.nomor_hp || '',
                alamat: driver.alamat || '',
                tanggal_bergabung: driver.tanggal_bergabung ? driver.tanggal_bergabung.split('T')[0] : '',
                status: driver.status || 'aktif',
                vehicle_id: driver.vehicle_id || ''
            });
        } else {
            setEditingDriver(null);
            setFormData({
                nama: '',
                nik: '',
                nomor_sim: '',
                jenis_sim: 'B2',
                tanggal_expired_sim: '',
                nomor_hp: '',
                alamat: '',
                tanggal_bergabung: new Date().toISOString().split('T')[0],
                status: 'aktif',
                vehicle_id: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                tanggal_expired_sim: formData.tanggal_expired_sim ? new Date(formData.tanggal_expired_sim).toISOString() : null,
                tanggal_bergabung: formData.tanggal_bergabung || new Date().toISOString().split('T')[0],
                vehicle_id: formData.vehicle_id || null
            };

            if (editingDriver) {
                await updateDriver(editingDriver.id, submitData);
                toast.success('Sopir berhasil diupdate');
            } else {
                await createDriver(submitData);
                toast.success('Sopir berhasil ditambahkan');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save driver:', error);
            toast.error('Gagal menyimpan data sopir');
        }
    };

    const handleDelete = async (driver) => {
        if (window.confirm(`Yakin ingin menghapus "${driver.nama}"?`)) {
            try {
                await deleteDriver(driver.id);
                toast.success('Sopir berhasil dihapus');
                fetchData();
            } catch (error) {
                console.error('Failed to delete driver:', error);
                toast.error('Gagal menghapus sopir');
            }
        }
    };

    const getDaysUntilExpiry = (dateStr) => {
        if (!dateStr) return null;
        const expiry = new Date(dateStr);
        const today = new Date();
        const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const filteredDrivers = drivers.filter(d =>
        d.nama.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.nomor_sim.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.nomor_hp.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const columns = [
        {
            header: 'Nama',
            accessor: 'nama',
            render: (value, row) => (
                <div>
                    <p className="font-medium">{value}</p>
                    <p className="text-xs text-muted-foreground font-mono">{row.nik}</p>
                </div>
            )
        },
        {
            header: 'SIM',
            accessor: 'nomor_sim',
            render: (value, row) => {
                const daysUntil = getDaysUntilExpiry(row.tanggal_expired_sim);
                const isExpiringSoon = daysUntil !== null && daysUntil <= 30;
                const isExpired = daysUntil !== null && daysUntil < 0;
                
                return (
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">{value}</span>
                            <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.jenis_sim}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                            {(isExpiringSoon || isExpired) && <AlertTriangle className="w-3 h-3 text-orange-500" />}
                            <span className={`text-xs ${isExpired ? 'text-red-500' : isExpiringSoon ? 'text-orange-500' : 'text-muted-foreground'}`}>
                                {isExpired ? 'Expired' : `Exp: ${formatDate(row.tanggal_expired_sim)}`}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'No. HP',
            accessor: 'nomor_hp',
            render: (value) => <span className="text-sm font-mono">{value}</span>
        },
        {
            header: 'Kendaraan',
            accessor: 'vehicle_nomor_polisi',
            render: (value) => <span className="text-sm font-mono">{value || '-'}</span>
        },
        {
            header: 'Bergabung',
            accessor: 'tanggal_bergabung',
            render: (value) => <span className="text-sm text-muted-foreground">{formatDate(value)}</span>
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (value) => {
                const statusMap = {
                    'aktif': 'completed',
                    'cuti': 'pending',
                    'resign': 'cancelled'
                };
                return <StatusBadge status={statusMap[value] || value} />;
            }
        },
        {
            header: '',
            accessor: 'actions',
            render: (_, row) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`driver-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(row)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onClick={() => handleDelete(row)}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Hapus
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout title="Sopir">
            <div className="space-y-6" data-testid="drivers-page">
                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex flex-1 gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Cari sopir..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-drivers"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Status</SelectItem>
                                {DRIVER_STATUSES.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={() => handleOpenDialog()} 
                        className="industrial-btn-primary"
                        data-testid="add-driver-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Sopir
                    </Button>
                </div>

                {/* Drivers Table */}
                <DataTable
                    columns={columns}
                    data={filteredDrivers}
                    loading={loading}
                    emptyMessage="Tidak ada sopir"
                    emptyIcon={Users}
                />

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="section-title">
                                {editingDriver ? 'Edit Sopir' : 'Tambah Sopir Baru'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingDriver ? 'Update informasi sopir.' : 'Masukkan data sopir baru.'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nama">Nama Lengkap *</Label>
                                    <Input
                                        id="nama"
                                        value={formData.nama}
                                        onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        data-testid="driver-nama-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nik">NIK *</Label>
                                    <Input
                                        id="nik"
                                        value={formData.nik}
                                        onChange={(e) => setFormData({ ...formData, nik: e.target.value })}
                                        required
                                        className="rounded-sm font-mono"
                                        maxLength={16}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nomor_sim">Nomor SIM *</Label>
                                    <Input
                                        id="nomor_sim"
                                        value={formData.nomor_sim}
                                        onChange={(e) => setFormData({ ...formData, nomor_sim: e.target.value })}
                                        required
                                        className="rounded-sm font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="jenis_sim">Jenis SIM *</Label>
                                    <Select 
                                        value={formData.jenis_sim} 
                                        onValueChange={(value) => setFormData({ ...formData, jenis_sim: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SIM_TYPES.map(t => (
                                                <SelectItem key={t} value={t}>{t}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tanggal_expired_sim">Expired SIM *</Label>
                                    <Input
                                        id="tanggal_expired_sim"
                                        type="date"
                                        value={formData.tanggal_expired_sim}
                                        onChange={(e) => setFormData({ ...formData, tanggal_expired_sim: e.target.value })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nomor_hp">Nomor HP *</Label>
                                    <Input
                                        id="nomor_hp"
                                        value={formData.nomor_hp}
                                        onChange={(e) => setFormData({ ...formData, nomor_hp: e.target.value })}
                                        required
                                        className="rounded-sm font-mono"
                                        placeholder="08xxxxxxxxxx"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tanggal_bergabung">Tanggal Bergabung</Label>
                                    <Input
                                        id="tanggal_bergabung"
                                        type="date"
                                        value={formData.tanggal_bergabung}
                                        onChange={(e) => setFormData({ ...formData, tanggal_bergabung: e.target.value })}
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Select 
                                        value={formData.status} 
                                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DRIVER_STATUSES.map(s => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vehicle_id">Kendaraan</Label>
                                    <Select 
                                        value={formData.vehicle_id || "none"} 
                                        onValueChange={(value) => setFormData({ ...formData, vehicle_id: value === "none" ? "" : value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue placeholder="Pilih kendaraan" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Tidak ada</SelectItem>
                                            {vehicles.filter(v => !v.driver_id || v.driver_id === editingDriver?.id).map(v => (
                                                <SelectItem key={v.id} value={v.id}>{v.nomor_polisi} - {v.merk}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="alamat">Alamat</Label>
                                    <Textarea
                                        id="alamat"
                                        value={formData.alamat}
                                        onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                                        className="rounded-sm"
                                        rows={2}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                    Batal
                                </Button>
                                <Button type="submit" className="industrial-btn-primary" data-testid="save-driver-btn">
                                    {editingDriver ? 'Update' : 'Simpan'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
